<template>
	<div class="action-editor-container" style="width:100%; height: 100%; display: flex;" @drop="onDrop">
		<VueFlow :connection-mode="ConnectionMode.Strict" :connection-radius="20" delete-key-code="Delete" @dragover="onDragOver" @dragleave="onDragLeave" @nodes-change="onNodesChange">
            <Background patternColor="#66666B" :style="{ backgroundColor: isDragOver ? '#e7f3ff' : 'transparent', transition: 'background-color 0.2s ease' }" />
			<Controls />
			<template #node-select="props">
				<TriggerSelect v-bind="props" />
			</template>
			<template #node-click="props">
				<TriggerClick v-bind="props" />
			</template>
			<template #node-contextmenu="props">
				<TriggerContextMenu v-bind="props" />
			</template>
			<template #node-show-dialog="props">
				<ActionShowDialog v-bind="props" />
			</template>
			<template #node-conditional="props">
				<ActionConditional v-bind="props" />
			</template>
			<template #node-navigate="props">
				<ActionNavigate v-bind="props" />
			</template>
			<template #node-component-msg="props">
				<ActionComponentMsg v-bind="props" />
			</template>
			<template #node-generate="props">
				<ActionGenerate v-bind="props" />
			</template>
			<template #node-mote-create="props">
				<ActionMoteCreate v-bind="props" />
			</template>
			<template #node-mote-delete="props">
				<ActionMoteDelete v-bind="props" />
			</template>
			<template #node-mote-update="props">
				<ActionMoteUpdate v-bind="props" />
			</template>
			<template #node-mote-clone="props">
				<ActionMoteClone v-bind="props" />
			</template>
			<template #node-call-n8n="props">
				<ActionCallN8n v-bind="props" />
			</template>
			<template #node-call-ai="props">
				<ActionCallAI v-bind="props" />
			</template>
        </VueFlow>
		<div class="action-editor-sidebar">
			<div class="nodes">
				<h4 class="nodes-header">Drag to add</h4>
				<div :class="{ 'vue-flow__node-input': true,  'flow-trigger-node': true, 'recommended': triggerRecommendations['select'] === true, 'not-recommended': triggerRecommendations['select'] === false }"
					:draggable="true" @dragstart="onDragStart($event, 'select')">Select</div>
				<div :class="{ 'vue-flow__node-input': true,  'flow-trigger-node': true, 'recommended': triggerRecommendations['click'] === true, 'not-recommended': triggerRecommendations['click'] === false }" 
					:draggable="true" @dragstart="onDragStart($event, 'click')">Click</div>
				<div :class="{ 'vue-flow__node-input': true,  'flow-trigger-node': true, 'recommended': triggerRecommendations['contextmenu'] === true, 'not-recommended': triggerRecommendations['contextmenu'] === false }" 
					:draggable="true" @dragstart="onDragStart($event, 'contextmenu')">Context Menu</div>
				<div class="vue-flow__node-default" :draggable="true" @dragstart="onDragStart($event, 'show-dialog')">Show Dialog</div>
				<div class="vue-flow__node-default" :draggable="true" @dragstart="onDragStart($event, 'conditional')">Conditional</div>
				<div class="vue-flow__node-default" :draggable="true" @dragstart="onDragStart($event, 'navigate')">Navigate</div>
				<div class="vue-flow__node-default" :draggable="true" @dragstart="onDragStart($event, 'component-msg')">Component Msg</div>
				<div class="vue-flow__node-default" :draggable="true" @dragstart="onDragStart($event, 'generate')">Generate Doc</div>
				<div class="vue-flow__node-default" style="background:#ffcfd6;" :draggable="true" @dragstart="onDragStart($event, 'mote-create')">Mote Create</div>
				<div class="vue-flow__node-default" style="background:#ffcfd6;" :draggable="true" @dragstart="onDragStart($event, 'mote-delete')">Mote Delete</div>
				<div class="vue-flow__node-default" style="background:#ffcfd6;" :draggable="true" @dragstart="onDragStart($event, 'mote-update')">Mote Update</div>
				<div class="vue-flow__node-default" style="background:#ffcfd6;" :draggable="true" @dragstart="onDragStart($event, 'mote-clone')">Mote Clone</div>
				<div class="vue-flow__node-default" style="background:linear-gradient(115deg, skyblue 50%, #e8eaeb 90%);" :draggable="true" @dragstart="onDragStart($event, 'call-n8n')">Call n8n</div>
				<div class="vue-flow__node-default" style="background:linear-gradient(115deg, skyblue 50%, #e8eaeb 90%);" :draggable="true" @dragstart="onDragStart($event, 'call-ai')">Query AI</div>
			</div>
			<div class="actions">
				<DxButton text="Delete" type="danger" @click="deleteAction" />
				<DxButton text="Save" type="default" @click="saveAction" />
			</div>
		</div>
		<DxLoadPanel
			:visible="isBusy"
			:message="busyMessage"
			:show-indicator="true"
			:show-pane="true"
			:shading="true"
			container=".action-editor-container"
		/>
	</div>
</template>

<script setup>
/* eslint-disable */
import { ref, defineProps, computed, defineEmits, nextTick, inject, watch, onMounted, toRaw } from 'vue';
import { VueFlow, useVueFlow, ConnectionMode, MarkerType } from '@vue-flow/core'
import { Background } from '@vue-flow/background'
import { Controls } from '@vue-flow/controls'
import { DxButton } from 'devextreme-vue/button'
import { DxLoadPanel } from 'devextreme-vue/load-panel'
import useDragAndDrop from './useDnD'
import { TRIGGER_TYPES } from '../../composables/useActionEngine'
import { alert as dxAlert, confirm as dxConfirm } from 'devextreme/ui/dialog';
import { ActionCache } from '../../actions.js';

import ActionShowDialog from './action-show-dialog.vue'
import ActionConditional from './action-conditional.vue'
import ActionNavigate from './action-navigate.vue'
import ActionComponentMsg from './action-component-msg.vue'
import ActionGenerate from './action-generate.vue'
import TriggerSelect from './trigger-select.vue'
import TriggerClick from './trigger-click.vue'
import TriggerContextMenu from './trigger-contextmenu.vue'
import ActionMoteCreate from './action-mote-create.vue'
import ActionMoteDelete from './action-mote-delete.vue'
import ActionMoteUpdate from './action-mote-update.vue'
import ActionMoteClone from './action-mote-clone.vue'
import ActionCallN8n from './action-call-n8n.vue'
import ActionCallAI from './action-call-ai.vue'

const { onDragOver, onDrop, onDragLeave, isDragOver, onDragStart, getActionNodeId } = useDragAndDrop()
const { onConnect, addEdges, getNodes, getEdges, setNodes, setEdges } = useVueFlow()
const $http = inject('$http');

const props = defineProps([
	'graphId',					// Graph ID to load, or null to create a new graph.
	'startupEvent',				// Automatic trigger event to start a new graph with. Must be set BEFORE graphId is changed.
]);
const emit = defineEmits(['editorFinished']);

// Loading state for operations
const isBusy = ref(false);
const busyMessage = ref('Loading...');

// Track trigger recommendations and not-recommendations based on startup event.
const disableStartupRecommendations = ref(false);		// Once we have dragged over a trigger, don't recommend it so much.
const triggerRecommendations = computed(() => {
	const recommendations = {};
	if (props.startupEvent && !disableStartupRecommendations.value) {		// If we have a startup event, recommend the trigger that matches it, and don't recommend the others.
		for (const trigger of Object.values(TRIGGER_TYPES)) {
			recommendations[trigger] = trigger === props.startupEvent;
		}
	}
	// TODO: Maybe do other triggers based on what is already present in the graph?
	return recommendations;
});

// Cycle detection using DFS
const hasCycle = (sourceId, targetId) => {
	// Get current nodes and edges from Vue Flow
	const currentNodes = getNodes.value;
	const currentEdges = getEdges.value;
	
	// Create adjacency list from current edges
	const adjacencyList = {};
	
	// Initialize adjacency list with all nodes
	currentNodes.forEach(node => {
		adjacencyList[node.id] = [];
	});
	
	// Add existing edges
	currentEdges.forEach(edge => {
		if (adjacencyList[edge.source]) {
			adjacencyList[edge.source].push(edge.target);
		}
	});
	
	// Add the potential new edge
	if (adjacencyList[sourceId]) {
		adjacencyList[sourceId].push(targetId);
	}
	
	// DFS with cycle detection
	const visited = new Set();
	const recStack = new Set();
	
	const dfs = (nodeId) => {
		if (recStack.has(nodeId)) {
			return true; // Cycle detected
		}
		if (visited.has(nodeId)) {
			return false; // Already processed
		}
		
		visited.add(nodeId);
		recStack.add(nodeId);
		
		for (const neighbor of adjacencyList[nodeId] || []) {
			if (dfs(neighbor)) {
				return true;
			}
		}
		
		recStack.delete(nodeId);
		return false;
	};
	
	// Check for cycles starting from each node
	for (const nodeId of Object.keys(adjacencyList)) {
		if (!visited.has(nodeId)) {
			if (dfs(nodeId)) {
				return true; // Cycle found
			}
		}
	}
	
	return false; // No cycle
};

const handleConnect = (params) => {
	// Disallow loopback connections.
	if (params.source === params.target) {
		return;
	}
	// Check for cycles before adding the edge
	if (hasCycle(params.source, params.target)) {
		console.warn('Connection would create a cycle. Connection rejected.');
		return;
	}
	// Add additional arrow marker to edges.
	const edgeWithMarker = {
		...params,
		markerEnd: { type: MarkerType.ArrowClosed, width: 20, height: 20, color: '#333' }
	}
	addEdges(edgeWithMarker)
}
onConnect(handleConnect)

// Deal with UI changes to graph based on changes to node.
const onNodesChange = (changes) => {
	changes.forEach(change => {
		// If we are adding a trigger node, stop the recommendation UI for our trigger nodes.
		if (change.type == 'add') {
			if (change.item.type && Object.values(TRIGGER_TYPES).includes(change.item.type)) {
				disableStartupRecommendations.value = true;
			}
		}
	});
};

// Initialize the graph with no nodes and edges, or a single node for startup trigger if we are creating a new graph.
// TODO: Deal with loading saved graph from backend, etc.
const initializeGraph = () => {
	const initialNodes = [
		// TODO: This doesn't work, the node gets added to graph but it seems to mess with the vertices for dragging edges from...
		// { id: initialNodeId, type: String(props.startupEvent), position: { x: 50, y: 50 }, data: {...}, },
		// I can't seem to find the fix easily -- this is some complicated bug with vue flow and/or the way we are using it in a popup.
		// So instead, we just restrict the trigger nodes to the one we want to start with and auto-fill their data when dragged the first time.
	];
	const initialEdges = [];
	setNodes(initialNodes);
	setEdges(initialEdges);
};

// Load graph data from backend
const loadGraphData = async (id) => {
	isBusy.value = true;
	busyMessage.value = 'Loading action...';
	try {
		const response = await $http.get(`/action/${id}`);
		console.log('Loading graph:', response);
		if (response.data && response.data.nodes && response.data.edges) {
			// Transform the data to match Vue Flow format
			const nodes = response.data.nodes.map(node => ({
				id: node.id,
				type: node.type,
				position: node.position,
				data: node.data || {},
				style: node.style || {}
			}));
			
			const edges = response.data.edges.map(edge => ({
				id: edge.id,
				source: edge.source,
				target: edge.target,
				markerEnd: { type: MarkerType.ArrowClosed, width: 20, height: 20, color: '#333' },
				style: edge.style || {}
			}));
			
			setNodes(nodes);
			setEdges(edges);
		}
	} catch (error) {
		console.error('Error loading graph data:', error);
		// Initialize with empty graph on error
		initializeGraph();
	} finally {
		isBusy.value = false;
	}
};

// Watch for graphId changes
watch(() => props.graphId, (newGraphId) => {
	if (newGraphId) {
		loadGraphData(newGraphId);
	} else {
		initializeGraph();
	}
}, { immediate: true });

// Initialize on mount
onMounted(() => {
	if (props.graphId) {
		loadGraphData(props.graphId);
	} else {
		initializeGraph();
	}
});

// Save handler - serialize graph and post it to the backend.
const saveAction = async () => {
	// Validation: the graph cannot contain more than one trigger node, and should have at least one.
	const triggerNodeCount = getNodes.value.filter(node => node.type && Object.values(TRIGGER_TYPES).includes(node.type)).length;
	if (triggerNodeCount > 1) {
		dxAlert(`<div class="dx-error-message"><i class="dx-icon-warning"></i><div class="dx-error-message-text">Actions can only have one trigger point from which they start.<br>Please remove all but one trigger node and try again.</div></div>`, 'Your action has more than one trigger!');
		return;
	} else if (triggerNodeCount <= 0) {
		dxAlert(`<div class="dx-error-message"><i class="dx-icon-warning"></i><div class="dx-error-message-text">Actions must have at least one trigger point from which they start.<br>Please add a trigger node and try again.</div></div>`, 'Your action has no triggers!');
		return;
	}
	// Validation: the graph has to have at least one edge (otherwise it's just a trigger node).
	if (getEdges.value.length <= 0) {
		dxAlert(`<div class="dx-error-message"><i class="dx-icon-warning"></i><div class="dx-error-message-text">Your actions must include some links between the trigger and nodes.<br>Please add some links between nodes and try again.</div></div>`, 'Your action has no flow links!');
		return;
	}

	// If this is a new action, we need to prompt for a name before we save.
	let graphName = props.graphId ? 'Untitled' : prompt('Please enter a name for your action.', 'Action Name?');
	if (!graphName && !props.graphId) {
		dxAlert(`<div class="dx-error-message"><i class="dx-icon-warning"></i><div class="dx-error-message-text">Your action must have a name.</div></div>`, 'Your action has no name!');
		return;
	}

	const serializedData = {
		label: graphName,
		nodes: getNodes.value.map(node => ({
			id: node.id,
			type: node.type,
			position: node.position,
			data: node.data || {},
			// style: node.style || {}
		})),
		edges: getEdges.value.map(edge => ({
			id: edge.id,
			source: edge.source,
			target: edge.target,
			// markerEnd: edge.markerEnd,
			// style: edge.style || {}
		}))
	};
	console.log('Serialized data for saving:', serializedData);

	isBusy.value = true;
	busyMessage.value = 'Saving action...';
	try {
		// If we have a graphId, update existing action, otherwise create new one
		if (props.graphId) {
			serializedData['_method'] = 'PUT';
			await $http.post(`/action/${props.graphId}`, serializedData);
		} else {
			await $http.post('/action', serializedData);
		}
		
		ActionCache.fetchActionGraph();
		await ActionCache.waitForData();
		isBusy.value = false;
		await dxAlert(`<div class="dx-success-message"><i class="dx-icon-check"></i><div class="dx-success-message-text">Action saved successfully!</div></div>`, 'Save Complete');
		emit('editorFinished');
	} catch (error) {
		console.error('Error saving action:', error);
		isBusy.value = false;
		dxAlert(`<div class="dx-error-message"><i class="dx-icon-warning"></i><div class="dx-error-message-text">${error.response?.data?.message || 'Failed to save action'}</div></div>`, `Error ${error.response?.status || '0'} / ${error.response?.statusText || 'Unknown'}`);
	}
}

// Delete handler - delete the action from the backend.
const deleteAction = async () => {
	// Prompt for additional confirmation from user.
	const confirmed = await dxConfirm('Are you sure you want to delete this action? This action cannot be undone.', 'Delete Action?');
	if (!confirmed) {
		return;
	}
	// Delete the action from the backend and refresh the action graph.
	isBusy.value = true;
	busyMessage.value = 'Deleting action...';
	await $http.delete(`/action/${props.graphId}`);
	ActionCache.fetchActionGraph();
	await ActionCache.waitForData();
	isBusy.value = false;
	emit('editorFinished');
}

</script>

<style lang="scss">
@import '@vue-flow/core/dist/style.css';
@import '@vue-flow/core/dist/theme-default.css';
@import '@vue-flow/controls/dist/style.css';

// Override default handle size of vue-flow
.vue-flow__handle {
	width: 10px;
	height: 10px;
}

.action-editor-sidebar {
	background-color: aliceblue;
	padding: 10px;
	display: flex;
	flex-direction: column;
	justify-content: space-between;

	.nodes-header {
		text-align: center;
		font-size: 12px;
		color: #444;
		margin: 0 0 10px 0;
		padding: 0 0 5px 0;
		border-bottom: 1px solid #aaa;
	}
	.nodes > div {
		text-transform: uppercase;
		margin-bottom: 10px;
		font-weight: 500;
		font-size: 14px;
		color: #333;
	}
	.actions .dx-button {
		width: 100%;
		margin-bottom: 10px;
	}
	.flow-trigger-node {
		background: papayawhip;
		&.not-recommended {
			opacity: 0.5;
		}
		&.recommended {
			border: 2px solid #4CAF50;
			animation: border-pulse 2s infinite;
			box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
		}
	}

	@keyframes border-pulse {
		0% {
			box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
		}
		70% {
			box-shadow: 0 0 0 10px rgba(76, 175, 80, 0);
		}
		100% {
			box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
		}
	}
}

.action-flow-node {
	width: 200px;
	padding: 12px;
	background: white;
	border: 1px solid #ddd;
	border-radius: 8px;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
	&.action-flow-node-trigger {
		background-color: papayawhip;
	}
	&.action-flow-node-danger {
		background: #ffcfd6;
	}
	&.action-flow-node-call {
		background: linear-gradient(105deg, skyblue, #e8eaeb);
	}

	.action-flow-title {
		font-weight: 600;
		font-size: 14px;
		color: #333;
		margin-bottom: 6px;
		text-align: center;
	}
	.action-flow-hint {
		font-size: 11px;
		font-style: italic;
		color: #666;
		text-align: center;
		line-height: 1.3;
		margin-bottom: 12px;
	}
	.action-flow-param {
		height: 36px;
		line-height: 36px;
		padding: 0 12px;
		margin: 4px 0;
		border-radius: 6px;
		font-size: 13px;
		font-weight: 500;
		transition: all 0.2s ease;
		border: 1px solid #e1e5e9;
		background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
		box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
		position: relative;
		
		&:hover {
			padding-top: 5px;
			box-shadow: 0 3px 8px rgba(0, 0, 0, 0.1);
			border-color: #cbd5e0;
		}
		
		&.param-tgt {
			background: linear-gradient(135deg, #e3f2fd 0%, #f3f9ff 100%);
			border-color: #90caf9;
			color: #1976d2;
			
			&:hover {
				background: linear-gradient(135deg, #bbdefb 0%, #e3f2fd 100%);
				border-color: #64b5f6;
			}
		}
		
		&.param-src {
			background: linear-gradient(135deg, #f3e5f5 0%, #faf5ff 100%);
			border-color: #ce93d8;
			color: #7b1fa2;
			
			&:hover {
				background: linear-gradient(135deg, #e1bee7 0%, #f3e5f5 100%);
				border-color: #ba68c8;
			}
		}

		&.param-config {
			background: linear-gradient(135deg, #f5f5f5 0%, #fafafa 100%);
			border-color: #d0d0d0;
			color: #666666;
			
			&:hover {
				background: linear-gradient(135deg, #e8e8e8 0%, #f0f0f0 100%);
				border-color: #b8b8b8;
			}
		}
	}
}

// Vue flow selection support for our custom nodes.
.vue-flow__node.selected .action-flow-node {
	outline: 2px solid #009688;
	box-shadow: 0 4px 12px rgba(0, 150, 136, 0.3);
}

</style>
